import {useState, useEffect, useRef} from 'react'
import {Link, useRouteLoaderData, useLocation, useNavigate, useFetcher} from 'react-router'
import {useModelStatus} from '@core/hooks/useModelStatusProvider'
import {deriveDSubState, type OwnerRun, type OwnerRunSlot} from '@core/packs/dSubState'
import {PackPageShell, PackGalleryRows, type PackPageSeoSection} from '@core/packs/PackPageShell'
import {Header} from '@product/narcis/components/Header/header'
import {buildPackJsonLd, packFaq} from './seo'
import {PackStepStrip} from './PackStepStrip'
import {PackModelChip} from './PackModelChip'
import {PackRelatedStrip} from './PackRelatedStrip'
import {PackGalleryCaption} from './PackGalleryCaption'
import {narcisTrack} from '@product/narcis/utils/analytics'
import {PackHeroStage, OWNER_GENERATE_LABEL} from './PackHeroStage'
import type {StageCodaAction, StageLookDoor} from './PackHeroStage'
import {packStageFor} from './packStage'
import {usePackQueue} from '@product/narcis/hooks/usePackQueue'
import {mintRequestId} from '@core/wall/requestId'
import {packContinueBody, batchWallPath, basisWallPath} from '@core/wall/pageVerb'
import {tileIdAt} from '@core/packs/stage/travel'
import {track} from '@core/utils/analytics'
import {IdentityIntake} from '@product/narcis/wall/IdentityIntake'
import {MIN_TRAINING_PHOTOS} from '@core/hooks/useModelIntake'
import {errorLineFor, noticeKindFor} from '@core/wall/ui/chains'
import {Notice} from '@ui/Notice'
import type {ErrorResponse} from '@core/wall/contract'
import type {LivePackDefinition, SerializedUserModel} from './types'
import type {loader as rootLoader} from '@root'
import {ABOUT_NARCIS} from './aboutNarcis'
import type {DisplayCurrency} from '@core/billing/displayCurrency'
import {CREATE_VERB_PAID, createVerb as priceVerb, HOW_IT_WORKS, PACK_HEADINGS, packLine, PACK_SUMMARY} from '@product/narcis/proposition'
import {ordinal} from './ordinal'
import {queueFullLabel} from './queueFullState'
import {ANON_QUEUE_CAP} from '@product/narcis/utils/anonPackQueue'
import styles from './PackLandingTemplate.module.scss'

// OwnerRun/OwnerRunSlot (the loader-resolved run payload) and the D sub-state derivation itself
// are graduated into `@core/packs/dSubState.ts` — shared with ovid, 2026-07-29
// (thinking/plans/2026_07_28_counter_ledger_and_pack_factorisation.md). `status` is exactly
// `packRun.server.ts`'s `resolveRunStatus` — 'in_progress' drives D-gen, 'complete' drives D-del,
// both derived from `boundCount` (bound-slot count), never a counter. `slots` is slot-ordered WITH
// HOLES (index 0..maxGenerations-1) — the slot grid renders from it directly. Absent (loader
// returns nothing) means no grant exists at all for this pack — state D, first visit, never
// generated.

interface RecentCompletedGrant {
    id: string
    createdAt: string
}

// Per-category eyebrow — handoff §12 v2 (2026-05-22 revision).
// Keyed by `cat` id from catalog.ts, or by pack.heroCopyCategory when set.
// The 'formal-portrait' virtual row is for lifestyle packs that read portrait-formal
// (e.g. cannes-black-tie) — set `heroCopyCategory: 'formal-portrait'` on those packs
// instead of remapping lifestyle globally. Fallback covers unknown categories.
//
// Spine réclame Wave 1: the tail holds just the bare category word (AI leaves the promise layer per
// word-world rule 1), composed at the render site into "{eyebrowTail} · {n} scenes" (n =
// pack.rows.length, computed, not hardcoded).
const EYEBROW_TAILS: Record<string, string> = {
    professional: 'professional',
    creative: 'editorial',
    lifestyle: 'lifestyle',
    'formal-portrait': 'portrait',
}

const EYEBROW_TAIL_FALLBACK = 'photo'

function getEyebrowTail(pack: LivePackDefinition, category: string): string {
    // heroCopyCategory overrides the catalog cat for copy selection only.
    const key = pack.heroCopyCategory ?? category
    return EYEBROW_TAILS[key] ?? EYEBROW_TAIL_FALLBACK
}

interface PackLandingTemplateProps {
    pack: LivePackDefinition
    /** The wall tile behind each look, in the stage's order (`pack.rows` flattened) — resolved by
     *  the loader; all-null when the wall has not ingested this pack (then no look door shows). */
    lookTileIds?: readonly (number | null)[]
    /** Short hub-friendly name from the catalog (e.g. "LinkedIn Headshots"). */
    hubName: string
    /** Human-readable category label (e.g. "Professional"). */
    categoryLabel: string
    /** Raw catalog category id (e.g. "professional") — drives the copy matrix. */
    category: string
    /** True when Start asks this viewer to pay: the verb carries the price and the canon's pack line
     *  stands under the title (`proposition.ts`). */
    asksPayment?: boolean
    /** The currency the price is written in while payment is asked. */
    currency?: DisplayCurrency
    /** The owner's run state for THIS pack (narcis_funnel plan §9) — absent means never started
     *  (state D); present means D-gen (in_progress) or D-del (complete). See `OwnerRun`'s own doc
     *  (`@core/packs/dSubState.ts`). */
    ownerRun?: OwnerRun
    /** Most recent completed grant for this user+pack — for returning-buyer banner. */
    recentCompletedGrant?: RecentCompletedGrant
    /** The signed-in user's trained model, if any (canvas 03). */
    userModel?: SerializedUserModel | null
    /** True for a signed-in user — tells the intake whether it must ask for sign-in. */
    isAuthenticated?: boolean
    /** State-A related-strip heading — pack-authored `relatedTitle` or the "More like this"
     *  fallback (relatedPacks.ts:resolveRelatedTitle, resolved server-side in the loader). */
    relatedTitle?: string
    /** Up to 4 related-pack cards (relatedPacks.ts:resolveRelatedPacks, resolved server-side —
     *  cover/title/category/nImages already joined from the catalog + registry). */
    relatedPacksCards?: {slug: string; title: string; cover: string; nImages: number; categoryLabel: string}[]
}

/** Format a date string as "Mon DD" (e.g. "Apr 12"). */
function formatBannerDate(iso: string): string {
    const d = new Date(iso)
    return d.toLocaleDateString('en-GB', {month: 'short', day: 'numeric'})
}


export function PackLandingTemplate({
    pack,
    lookTileIds = [],
    hubName,
    categoryLabel,
    category,
    asksPayment = false,
    currency = 'usd',
    ownerRun: ownerRunProp,
    recentCompletedGrant,
    userModel: userModelProp,
    isAuthenticated = false,
    relatedTitle = 'More like this',
    relatedPacksCards = [],
}: PackLandingTemplateProps) {
    // Content/state split plan (thinking/plans/2026_08_11_content_state_split.md), Phase 1 steps
    // 3-5. `userModel` is the loader's first-paint seed, superseded once the model-status context
    // (mounted once in `_layout.tsx`, polling `/api/model-status` while training/retraining/
    // ready-undelivered) resolves a fresher read — a React context update re-renders this component
    // when the poll resolves, which keeps the trainbar's live % moving (heroState === 'C'),
    // including a training finish observed in another tab. `ownerRun` is the D-gen
    // slot-status projection, re-fetched from `/api/pack/run-status?grant=<id>` while
    // `ownerSubState === 'D-gen'` (see the re-attach poll effect below) instead of the whole route
    // revalidating.
    const liveModel = useModelStatus()
    const userModel = userModelProp ? (liveModel ? {...userModelProp, ...liveModel} : userModelProp) : userModelProp
    const [ownerRun, setOwnerRun] = useState(ownerRunProp)
    useEffect(() => {
        setOwnerRun(ownerRunProp)
    }, [ownerRunProp])

    const eyebrowTail = getEyebrowTail(pack, category)
    // The no-model verb (pack page button, the stage's, the coda's): the canon's, priced while
    // payment is asked (`proposition.ts`).
    const createVerb = asksPayment ? priceVerb(currency) : CREATE_VERB_PAID
    const jsonLdSchemas = buildPackJsonLd(pack)

    // ── Hero-band state (narcis_funnel plan, Architecture decision 1) ──────────────────────────
    // `visitor_state` is server-derived once in the root loader (app/root.tsx) and exposed here —
    // one derivation, one source of truth. `heroState` below is the one axis every copy branch in
    // this component reads: anon → A · signed-no-model → B · training → C · owner → D.
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    const visitorState = rootData?.visitorState ?? 'anon'
    const heroState: 'A' | 'B' | 'C' | 'D' =
        visitorState === 'owner' ? 'D' :
        visitorState === 'training' ? 'C' :
        visitorState === 'signed' ? 'B' : 'A'

    // Owner sub-states (narcis_funnel plan §9, 2026-07-25 settlement — canvas 02 D/D-gen/D-del;
    // rule graduated into `@core/packs/dSubState.ts`, shared with ovid, 2026-07-29). `ownerRun` is
    // resolved server-side by the loader from PackGrant+PackConsumption (no client derivation, no
    // new client state) — see packRun.server.ts's header doc for the state-resolution contract
    // this mirrors exactly. D is first-visit-only: once a run exists (even a legacy partial one
    // the run driver is healing), the page never returns to D.
    const dSubState = heroState === 'D' ? deriveDSubState(ownerRun, pack.grid.nImages) : null
    const ownerSubState: 'D' | 'D-gen' | 'D-del' | null =
        dSubState === null ? null :
        dSubState.label === 'never_started' ? 'D' :
        dSubState.label === 'rendering' ? 'D-gen' : 'D-del'
    const stage = packStageFor(heroState, ownerSubState)

    // The wall write C's "Queue this pack →", D's first-time "Wear all 9 · from your model →", AND
    // D-del's "Wear it again"/"Re-render this pack →" regenerate all share (narcis wall plan step 6
    // § 3, extended to regenerate 2026-09-24 — the last old-style path, `ensureRunGrant`'s
    // `forceRegrant`/`driveGrantRun`, retired the same pass): `POST /continue/pack` with an empty
    // direction for the pack's nine tiles, then navigate to `/?batch=<batchId>` — the same shape
    // Toile's own `StageComponent.tsx` posts (`packContinueBody`/`batchWallPath`,
    // `@core/wall/pageVerb`), reused rather than hand-rolled a second time. A regenerate is exactly
    // this: a FRESH batch (new `requestId`, new `batchId`) on the SAME nine wall tiles — each tile's
    // chain gains a new take rather than anything being overwritten or discarded; the prior nine
    // stay reachable (the pack page's own `resolveDeliveredPackPhotos` — see that module's "newest
    // delivery wins" doc — surfaces whichever of a grant or a batch is more recent, so an old grant
    // from before this migration can no longer hide a fresh regenerate batch). No sign-in round trip
    // is needed here the way Toile's page verb needs one: heroState C and D both require an existing
    // `userId` by construction (a model row belongs to a signed-in account), so this is never
    // reachable signed out.
    //
    // One press, one batch. The guard below stays armed from the press until this page unmounts —
    // NOT until the response: `navigate` to the wall waits on the wall's own loader (a page of
    // tiles and `/mine`), a couple of seconds during which this page is still on screen. Observed
    // on dev 2026-09-22: two `POST /continue/pack` 1.6 s apart, two fresh `request_id`s, eighteen
    // renders of one pack — the idempotency key is per press, so only the page can hold the door.
    // `wallPackBusy` is the same fact for the eye (label and disabled state); the ref is the guard.
    // Both reset only on a failed post, where the button has to work again.
    const wallPackInFlightRef = useRef(false)
    const [wallPackBusy, setWallPackBusy] = useState(false)
    const [wallPackNote, setWallPackNote] = useState<string | null>(null)
    async function fireWallPackContinue(trigger: 'one_click' | 'hub_autostart' | 'queue' | 'regenerate') {
        if (wallPackInFlightRef.current) return
        wallPackInFlightRef.current = true
        setWallPackBusy(true)
        setWallPackNote(null)
        // generate (funnel spec §2) — every trigger that actually renders (first-time D, and since
        // 2026-09-24 the D-del regenerate too, matching the pre-wall `regenerateOwnerRun`'s own
        // `trigger: 'regenerate'` call); the C-state queue write has no equivalent in the 17-event
        // dictionary and fires nothing (GA4 is out of scope for this plan — thread `narcis_wall`,
        // 2026-09-21).
        if (trigger !== 'queue') {
            narcisTrack('generate', {source: 'pack', style_ref: pack.slug, trigger})
        }
        try {
            const res = await fetch('/continue/pack', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(packContinueBody(pack.slug, '', mintRequestId())),
                credentials: 'same-origin',
            })
            if (!res.ok) {
                let error: ErrorResponse | null = null
                try {
                    error = (await res.json()) as ErrorResponse
                } catch {
                    // A body-less 4xx/5xx: the status alone names the line.
                }
                setWallPackNote(errorLineFor(res.status, error?.error, error?.field))
                wallPackInFlightRef.current = false
                setWallPackBusy(false)
                return
            }
            const data: {batch_id: string} = await res.json()
            navigate(batchWallPath(data.batch_id))
        } catch {
            setWallPackNote(errorLineFor(0))
            wallPackInFlightRef.current = false
            setWallPackBusy(false)
        }
    }

    // Hub "Generate all 9 →" auto-fire (narcis_funnel plan, step 5; funnel spec §8 — the owner-hub
    // card button "fires generation AND navigates to the pack page mid-stream"). Narcis wall plan
    // step 6 § 3: the pack's nine now land on the wall, so this fires the same wall write the D
    // primary CTA does instead of the old grant-start action. Only fires for ownerSubState 'D'
    // (never started); a run already in progress or complete has nothing to auto-start.
    const autoStartFiredRef = useRef(false)
    useEffect(() => {
        if (ownerSubState !== 'D' || autoStartFiredRef.current) return
        if (typeof window === 'undefined') return
        const params = new URLSearchParams(window.location.search)
        if (params.get('autogenerate') !== '1') return
        autoStartFiredRef.current = true
        params.delete('autogenerate')
        const qs = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
        void fireWallPackContinue('hub_autostart')
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fire-once guard is the ref, not deps
    }, [ownerSubState])

    // "Its first looks" queue (narcis_funnel plan, Architecture decision 2/3) — anon localStorage
    // pre-signup, DB-backed once authenticated. Read once per page; queuePack/unqueuePack drive
    // the B-state ghost and C-state queue chip/CTA/remove-from-queue note.
    const packQueue = usePackQueue(visitorState)
    const queueEntry = packQueue.entryFor(pack.slug)

    // Map sample image src → alt so the wall-row <img> tags below get descriptive alt text.
    // pack.rows[].portraits[] is a flat string[] of URLs; pack.samples.images[] carries the alts
    // (rewritten 2026-05-08 for image SEO). Same UUIDs on both sides — Map lookup is O(1).
    const altBySrc = new Map(pack.samples.images.map((img) => [img.src, img.alt] as const))

    // Returning-buyer banner dismissal (session-scoped)
    const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
        if (typeof sessionStorage === 'undefined' || !recentCompletedGrant) return false
        return sessionStorage.getItem(`pack-returning-buyer-dismissed-${recentCompletedGrant.id}`) === '1'
    })

    function handleDismissBanner() {
        if (!recentCompletedGrant) return
        sessionStorage.setItem(`pack-returning-buyer-dismissed-${recentCompletedGrant.id}`, '1')
        setBannerDismissed(true)
    }

    // Owner's "re-render this pack" (copy spec §3c, the mirror rule's one exception to "the
    // composer shows the owner's nine forever"). Moved onto the wall 2026-09-24 (narcis wall plan
    // step 6, regenerate migration — the grant-minting `ensureRunGrant`'s `forceRegrant` path this
    // used from 2026-07-25 is retired for narcis): fires the SAME `fireWallPackContinue` write
    // "Wear all 9" uses, under its own 'regenerate' trigger (fires `generate` with `trigger:
    // 'regenerate'`, unchanged event shape — see `fireWallPackContinue`'s own doc). The CURRENT nine
    // stay exactly where they are; the new nine land in a fresh batch this same tile set's next
    // delivery resolves to. No soft-confirm: nothing is discarded.
    function handleRegenerate() {
        void fireWallPackContinue('regenerate')
    }

    // D-del "Download all N →" (copy spec §3c). Same-origin URLs (NARCIS_GENERATIONS_BASE_URL is
    // same-domain in every deployed environment), so the `download` attribute works without a
    // fetch+blob round trip — staggered 200ms apart, since browsers throttle/block a burst of
    // simultaneous same-tick downloads triggered without an intervening user gesture per file.
    function handleDownloadAll() {
        if (!ownerRun) return
        narcisTrack('pack_download', {pack_slug: pack.slug, scope: 'all'})
        const boundSlots = ownerRun.slots.filter((s): s is OwnerRunSlot & {imageUrl: string} => s.imageUrl !== null)
        boundSlots.forEach((slot, i) => {
            setTimeout(() => {
                const a = document.createElement('a')
                a.href = slot.imageUrl
                a.download = `${pack.slug}-${slot.slotIndex + 1}.webp`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
            }, i * 200)
        })
    }

    // Left-column CTA — heroState-driven label + action (copy spec §3). Mirrors the stage's own
    // CTA (PackHeroStage) so the two are always in sync — see that file's ctaLabel for the
    // matching derivation. The left CTA fires pack_cta_clicked (see handleLeftCtaClick below).
    const leftCtaLabel: string = (() => {
        // The wall write is out and the page is about to leave — one line for both D and C.
        if (wallPackBusy) return 'Starting …'
        if (heroState === 'D') {
            // D-gen/D-del (funnel spec §9, copy spec §3c). Canon transform: the spec's own
            // "Rendering — {n} of 9" em dash purged to a middot, matching the label-pair pattern
            // AMENDMENTS.md already applies everywhere else on this page.
            if (ownerSubState === 'D-gen') return `Working · ${ownerRun?.boundCount ?? 0} of ${pack.grid.nImages}`
            if (ownerSubState === 'D-del') return `Download all ${pack.grid.nImages} →`
            // The same label as the stage's 'model_ready' CTA — one function for both.
            return OWNER_GENERATE_LABEL
        }
        if (heroState === 'C') {
            if (queueEntry) return `Queued · ${ordinal(queueEntry.position)} in line`
            // Operator fix (DEVIATIONS.md #44): this CTA called queuePack with zero cap
            // awareness — at PACK_QUEUE_CAP the server silently refused and the button did
            // nothing. The tray remains the place to unqueue.
            if (packQueue.isFull) return queueFullLabel(ANON_QUEUE_CAP)
            // Operator fix (DEVIATIONS.md #44): the authored copy spec's full-length CTA ("Queue
            // this pack · renders when your model is ready →") wrapped to two lines in the yolk
            // pill. Shortened to a one-line form — the promise stays in the tagline above the CTA
            // ("all {n} photos arrive the moment it's ready, and we email them to you").
            return 'Queue this pack →'
        }
        return createVerb
    })()
    const leftCtaDisabled =
        // disabled once this pack is actually queued, OR the queue has no room left for it
        // (operator fix, DEVIATIONS.md #44).
        heroState === 'C' ? !!queueEntry || packQueue.isFull || wallPackBusy :
        // D-gen: rendering, server-owned — nothing for a click to do (funnel spec §9). Busy: the
        // wall write is out (see `fireWallPackContinue`'s own guard).
        heroState === 'D' ? ownerSubState === 'D-gen' || wallPackBusy :
        false
    const location = useLocation()
    const navigate = useNavigate()
    // The intake's sign-in comes back to this page as it stands, with `intake=1` to reopen it.
    const intakeParams = new URLSearchParams(location.search)
    intakeParams.set('intake', '1')
    const intakeReturnTo = `${location.pathname}?${intakeParams}`
    // "Train your model →" opens the intake HERE (plan 2026_09_25_narcis_pack_coda.md — the whole
    // pack trains from its own page): the visitor keeps the pack they came for in view, the intake
    // asks sign-in only at Start and returns to this page (`?intake=1` reopens it; the photos wait
    // in the intake's own sessionStorage stash). Once training starts, this pack is queued on the
    // wall and the page leaves for it — the wall is the receipt, not the gate.
    const [intakeOpen, setIntakeOpen] = useState(false)
    function openIntake() {
        setIntakeOpen(true)
    }
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get('intake') !== '1') return
        params.delete('intake')
        const qs = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
        // Back from the sign-in with a model already on the account: the page's own CTA says what
        // to do next; there is nothing left to train.
        if (heroState === 'A' || heroState === 'B') setIntakeOpen(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once against the mount URL
    }, [])
    function closeIntake(result?: {queued: true}) {
        setIntakeOpen(false)
        if (result?.queued) void fireWallPackContinue('queue')
    }

    // B-state ghost CTA — "+ Queue this pack for your model" (copy spec §3 state B), context
    // pre_signup regardless of anon/signed-no-model per the spec's own event annotation. The
    // dictionary's pack_queued is the load-bearing signal here — usePackQueue's queuePack fires
    // it client-side once the queue action resolves, on both the anon and authed path (see
    // usePackQueue.ts) — no click-location breadcrumb needed on top of it (decision 8).
    function handleQueueGhostClick() {
        void packQueue.queuePack(pack.slug, 'pre_signup')
    }
    // C-state ghost — "Browse more styles while it trains →" (copy spec §3 state C). No dictionary
    // event for a plain nav click (decision 8) — the old nav_click breadcrumb has no equivalent
    // on this surface (header.tsx's own nav_click, a site-wide chrome event, is untouched).
    function handleBrowseGhostClick() {
        navigate('/packs')
    }
    // C-state note — "Remove from queue" (copy spec §3 state C).
    function handleRemoveFromQueueClick() {
        void packQueue.unqueuePack(pack.slug)
    }

    function handleLeftCtaClick() {
        // Owner sub-states (funnel spec §9) are server-owned: the first render lands on the wall
        // (see `fireWallPackContinue`'s own doc), D-del downloads, and D-gen is unreachable here
        // (the button is disabled, `leftCtaDisabled` above).
        if (heroState === 'D') {
            if (ownerSubState === 'D') void fireWallPackContinue('one_click')
            else if (ownerSubState === 'D-del') handleDownloadAll()
            return
        }
        if (heroState === 'C') {
            // Narcis wall plan step 6 § 3: "Queue this pack →" now writes the wall's own nine rows
            // (held by the training gate's `claimFilter` until the model is ready) and leaves for
            // the wall immediately — the old `PackQueueItem` write below this pack page verb no
            // longer runs. `queueEntry`/`packQueue.isFull` still gate the OLD system's own "Queued"
            // label (retirement is a later cleanup, plan step 5's own note); harmless here since
            // this write never sets `queueEntry`.
            if (queueEntry || packQueue.isFull) return
            void fireWallPackContinue('queue')
            return
        }
        openIntake()
    }

    // Re-attach polling for D-gen (narcis_funnel plan §9 — "every load of the pack page re-
    // attaches ... rendered slots filled, in-flight slot shimmering, rest as dots"). Content/state
    // split plan, Phase 1 step 5: polls the small `/api/pack/run-status?grant=<id>` resource route
    // (merging fresh slots into the local `ownerRun` state via `setOwnerRun`, declared near this
    // component's top) instead of revalidating the whole pack-page route tree. Fires once
    // immediately on arming — this poll is also today's presence-stamp site, moved from the old
    // pack-page loader hit (see `api.pack.run-status.tsx`'s header doc) — plus every 3s while
    // visible. Stops the instant `ownerRun.status` flips to 'complete': `ownerSubState` (derived
    // from `ownerRun` below) reads 'D-del' on the next render, so the effect's own dependency
    // tears the interval down and no further ticks fire.
    const runStatusFetcher = useFetcher<{run: OwnerRun | null}>()
    useEffect(() => {
        if (runStatusFetcher.data?.run) setOwnerRun(runStatusFetcher.data.run)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- react only to fresh fetcher data
    }, [runStatusFetcher.data])
    useEffect(() => {
        // Step-5 review S3: a batch-sourced run carries no `grantId` (the delivery lives on the
        // wall, not the paid ledger) — `/api/pack/run-status?grant=` has nothing to resolve for it,
        // so the poll simply never arms. The wall arm itself drives this run to completion; nothing
        // on this page needs to poll it.
        if (ownerSubState !== 'D-gen' || !ownerRun || !ownerRun.grantId) return
        const grantId = ownerRun.grantId
        function tick() {
            if (document.visibilityState === 'visible') runStatusFetcher.load(`/api/pack/run-status?grant=${grantId}`)
        }
        tick()
        const timer = setInterval(tick, 3000)
        document.addEventListener('visibilitychange', tick)
        return () => {
            clearInterval(timer)
            document.removeEventListener('visibilitychange', tick)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetcher.load identity churns; arm strictly on ownerSubState/grantId
    }, [ownerSubState, ownerRun?.grantId])

    // pack_render_resumed (funnel spec §9/§2): fires on a mount that finds a run already partway
    // done — "resumed" reads as "re-attaching after an absence," which a fresh page mount with
    // slots already rendered inherently is (a continuously-open tab never remounts). Guarded to
    // fire once per mount, not on every 3s poll tick above.
    const renderResumedFiredRef = useRef(false)
    useEffect(() => {
        if (renderResumedFiredRef.current) return
        if (ownerSubState !== 'D-gen' || !ownerRun || ownerRun.boundCount === 0) return
        renderResumedFiredRef.current = true
        narcisTrack('pack_render_resumed', {pack_slug: pack.slug, slots_done: ownerRun.boundCount})
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fire-once guard is the ref, not deps
    }, [ownerSubState])

    // pack_ready_seen (decision 2, 2026-07-28): the completed grid renders to a present user.
    // first_view distinguishes the completion moment (this mount watched D-gen before landing on
    // D-del) from a later revisit (the page loads straight into D-del) — sawRenderingRef is set
    // the instant this mount observes D-gen, so a load that never passes through D-gen reads as a
    // revisit. No persisted flag; both refs reset on every fresh mount.
    const sawRenderingRef = useRef(false)
    const packReadySeenFiredRef = useRef(false)
    useEffect(() => {
        if (ownerSubState === 'D-gen') sawRenderingRef.current = true
        if (packReadySeenFiredRef.current || ownerSubState !== 'D-del') return
        packReadySeenFiredRef.current = true
        narcisTrack('pack_ready_seen', {pack_slug: pack.slug, slot_count: pack.grid.nImages, first_view: sawRenderingRef.current})
    }, [ownerSubState])

    // Whether the returning-buyer banner should show:
    // signed in, completed grant exists, no ?id=, not dismissed
    const showReturningBanner =
        recentCompletedGrant != null &&
        !bannerDismissed

    // The coda (plan 2026_09_25_narcis_pack_coda.md): the tenth frame holds the page's primary
    // press — the left CTA's label, state and handler, so the two can never disagree. The stage
    // shows it only on its browsing stages (the curated nine), never over a run.
    const coda: StageCodaAction | null = heroState === 'D' && ownerSubState !== 'D' ? null : {
        lead: 'You, in',
        head: hubName,
        sub:
            heroState === 'D' ? 'Made from your model.' :
            heroState === 'C' ? 'We are creating your model. Queue this pack and it is made as soon as your model is ready.' :
            `Add at least ${MIN_TRAINING_PHOTOS} photos of yourself. Once your model is created, we make this pack for you.`,
        label: leftCtaLabel,
        disabled: leftCtaDisabled,
        onPress: handleLeftCtaClick,
    }

    // The look door — one picture, not the set: the wall armed on that look. Without a model the
    // wall opens the intake over it (`?intake=1`, the wall's own deep link, which asks sign-in
    // first); training or ready, the look is armed (`basisWallPath`) for Queue or Generate.
    const lookDoor: StageLookDoor = {
        label: heroState === 'D' ? 'Change this photo' : 'Get this photo of me',
        hrefAt: (i) => {
            const tileId = tileIdAt(lookTileIds, i)
            if (tileId === null) return null
            return heroState === 'C' || heroState === 'D' ? basisWallPath(tileId) : `/?arm=${tileId}&intake=1`
        },
        onFollow: (i) => track('pack_look_change', {pack_slug: pack.slug, look_index: i}),
    }

    // seoSections (SEO content backbone, packs/CLAUDE.md) — style guide → prep tips → FAQ (iff
    // non-empty) → use cases → About, one PackPageShell section each; order and per-field mapping
    // are exactly the render this replaces (verified byte-identical by the SSR snapshot guard).
    const seoSectionCandidates: (PackPageSeoSection | false)[] = [
        !!pack.seo.styleGuide && {
            key: 'styleGuide',
            title: pack.seo.styleGuide.title,
            paragraphs: pack.seo.styleGuide.paragraphs,
        },
        !!pack.seo.prepTips && {
            key: 'prepTips',
            title: pack.seo.prepTips.title,
            paragraphs: pack.seo.prepTips.intro ? [pack.seo.prepTips.intro] : undefined,
            steps: pack.seo.prepTips.steps,
        },
        {
            key: 'faq',
            title: 'Frequently asked questions',
            faq: packFaq(pack),
        },
        !!pack.seo.useCases && {
            key: 'useCases',
            title: pack.seo.useCases.title,
            bullets: pack.seo.useCases.items,
        },
        {
            key: 'about',
            title: ABOUT_NARCIS.title,
            paragraphs: [ABOUT_NARCIS.paragraph],
        },
    ]
    const seoSections = seoSectionCandidates.filter((s): s is PackPageSeoSection => !!s)

    return (
        <PackPageShell
            styles={styles}
            jsonLd={jsonLdSchemas}
            preloadImages={[pack.cover_portrait]}
            srOnly={{
                // The opening and the headings are the template's (`proposition.ts`); the pack's
                // own paragraphs follow.
                h1: pack.hero.headline,
                h2s: PACK_HEADINGS,
                paragraphs: [PACK_SUMMARY, ...pack.srOnly.paragraphs],
            }}
            header={<Header variant="standard" accent="packs" />}
            eyebrow={
                <>/{pack.slug} · {eyebrowTail} · {pack.rows.length} scenes</>
            }
            h1={pack.hero.headline}
            asideBeforeTagline={
                // Queue chip — copy spec §3 state C, only once THIS pack is actually queued (the
                // badge would lie otherwise). Sits between H1 and tagline per canvas 02-C.
                heroState === 'C' && queueEntry && (
                    <span className={styles.queueChip}>
                        <span className={styles.queueChipDot} aria-hidden="true" />
                        queued · yours once your model is ready
                    </span>
                )
            }
            tagline={
                // Tagline — copy matrix §3, per heroState. Only A carries pack-authored scene
                // prose (pack.hero.lede/subhead); B/C/D taglines are shared canon, verbatim, per
                // state.
                <>
                    {heroState === 'D' && ownerSubState === 'D' && (
                        // Operator step-1 gate correction (AMENDMENTS.md #3): "instantly"
                        // replaces "each in about ten seconds" — sceneShort tail unchanged.
                        <>
                            <strong>From your model, {pack.grid.nImages} {(categoryLabel || 'lifestyle').toLowerCase()} shots of you</strong>, instantly.
                            {pack.sceneShort ? ` ${pack.sceneShort}` : ''}
                        </>
                    )}
                    {/* D-gen (copy spec §3c) — workingLine (pack-authored, scene-specific)
                        + slot-count progress. Canon transform: spec's em dash purged to a
                        period split (AMENDMENTS.md's "prose" pattern), matching the rest
                        of this file's already-shipped purge. */}
                    {heroState === 'D' && ownerSubState === 'D-gen' && (
                        <>
                            <strong>{pack.workingLine ?? 'Your model is on set now.'}</strong>{' '}
                            {ownerRun?.boundCount ?? 0} of {pack.grid.nImages} are done. The rest finish
                            whether this page is open or not.
                        </>
                    )}
                    {/* D-del (copy spec §3c, the mirror rule) — ownedLine (or the composed
                        "{n} photos of you, in {scene}." fallback) + the render date. */}
                    {heroState === 'D' && ownerSubState === 'D-del' && ownerRun && (
                        <>
                            <strong>{pack.ownedLine ?? `${pack.grid.nImages} photos of you, in ${pack.sceneShort ?? 'this light'}.`}</strong>{' '}
                            Made by your model on {formatBannerDate(ownerRun.createdAt)}.
                        </>
                    )}
                    {heroState === 'C' && (
                        <>We are creating your model. This pack is queued: all {pack.grid.nImages} photos arrive when it is ready, and we email you.</>
                    )}
                    {heroState === 'B' && (
                        <>
                            <strong>Create your model once</strong>{' '}
                            <span className={styles.detailHero__desktopOnly}>for all {pack.grid.nImages} photos — and every other pack on Narcis.</span>
                            {/* Mobile truncation §3b: drops the tail after the bold clause. */}
                            <span className={styles.detailHero__mobileOnly}>for all {pack.grid.nImages} photos.</span>
                        </>
                    )}
                    {heroState === 'A' && (
                        <>
                            <strong>Create your model, and all {pack.grid.nImages} photos are of you.</strong>{' '}
                            {pack.hero.lede ?? pack.hero.subhead}
                        </>
                    )}
                </>
            }
            asideAfterTagline={
                <>
                    {/* The at-a-glance stat pills ("{n} looks · {n} scenes") were retired here
                        2026-08-10 (operator): the counts already reach the visitor from the
                        composer's own status chip, and a second pill row restated them in the
                        one column that should carry the argument. Removed as a concept, not
                        re-scoped to another state. */}

                    {/* The canon's pack line (PROPOSITION.md § 7, "Pack page, under the title") —
                        where the price row stood, for a visitor with no model (A/B) while payment
                        is asked. A pack carries no price of its own: the one price is the
                        model's. Training (C) and owners (D) are past the sale. */}
                    {asksPayment && (heroState === 'A' || heroState === 'B') && (
                        <p className={styles.detailHero__packLine}>{packLine(currency)}</p>
                    )}

                    {/* Left CTA — shown on all viewports in the left column (under price, above step strip).
                        On desktop (≥1024) this is the single primary CTA; the stage's ctaButton is hidden on desktop.
                        On mobile (<1024) this appears below the stage as a secondary reinforcement — but since
                        the stage button remains the primary on mobile, this is hidden on mobile via ctaMobileOnly. */}
                    <button
                        type="button"
                        className={`${styles.detailHero__cta} ${heroState === 'C' ? '' : styles.detailHero__ctaMobileHidden} ${leftCtaDisabled ? styles.detailHero__ctaDisabled : ''}`}
                        aria-disabled={leftCtaDisabled}
                        onClick={leftCtaDisabled ? undefined : handleLeftCtaClick}
                    >
                        <span className={styles.detailHero__ctaInner}>
                            <span>{leftCtaLabel}</span>
                        </span>
                    </button>

                    {/* The wall write's own failure line (narcis wall plan step 6 § 3) — the C/D
                        primary CTA's only client-visible error surface; a landed press navigates
                        away, so there is nothing else to show here on success. */}
                    {wallPackNote && (heroState === 'C' || heroState === 'D') && (
                        <Notice kind={noticeKindFor(wallPackNote)} className={styles.wallPackNote} onClear={() => setWallPackNote(null)}>
                            {wallPackNote}
                        </Notice>
                    )}

                    {/* B ghost — "+ Queue this pack for your model" (copy spec §3 state B).
                        Visible on mobile too (unlike the CTA above, which the stage's own
                        ctaBar duplicates <1024 for B — see PackHeroStage). */}
                    {heroState === 'B' && (
                        <button
                            type="button"
                            className={`${styles.queueGhost} ${queueEntry ? styles.queueGhostOn : ''}`}
                            aria-pressed={!!queueEntry}
                            // Operator fix (DEVIATIONS.md #44): this button called queuePack
                            // with zero cap awareness — at PACK_QUEUE_CAP the server
                            // silently refused and the button did nothing. The tray
                            // remains the place to unqueue.
                            onClick={queueEntry || packQueue.isFull ? undefined : handleQueueGhostClick}
                            disabled={!!queueEntry || packQueue.isFull}
                        >
                            {queueEntry
                                ? `✓ Queued · ${ordinal(queueEntry.position)}`
                                : packQueue.isFull
                                  ? queueFullLabel(ANON_QUEUE_CAP)
                                  : '+ Queue this pack for your model'}
                        </button>
                    )}
                    {/* C ghost — "Browse more packs while your model is created →" (spine
                        réclame Wave 1: "styles" is refused in core register). Mobile-
                        visible: the stage has no CTA row at all for waiting_model (see
                        PackHeroStage), so the left column is the CTA/ghost/note surface
                        on every breakpoint for C. */}
                    {heroState === 'C' && (
                        <button
                            type="button"
                            className={styles.queueGhost}
                            onClick={handleBrowseGhostClick}
                        >
                            Browse more packs while your model is created →
                        </button>
                    )}

                    {/* Notes — per heroState (copy spec §3). */}
                    {heroState === 'B' && (
                        <p className={styles.detailHero__note}>
                            <em>You sign in when you pay</em> — your queue is kept.{' '}
                            <span className={styles.detailHero__desktopOnly}>
                                Your photos are ready in a few hours. We email you when
                                they are ready.
                            </span>
                        </p>
                    )}
                    {heroState === 'C' && queueEntry && (
                        <p className={styles.detailHero__note}>
                            Changed your mind?{' '}
                            <button type="button" className={styles.inlineLinkButton} onClick={handleRemoveFromQueueClick}>
                                <em>Remove from queue</em>
                            </button>
                        </p>
                    )}
                    {/* Operator step-1 gate correction (AMENDMENTS.md #5). D-gen/D-del notes
                        are new this pass (copy spec §3c) — canon-purged (colon/period, no
                        em dash). */}
                    {heroState === 'D' && ownerSubState === 'D' && (
                        <p className={styles.detailHero__note}>
                            Saved to <em>My Packs</em>. Safe to leave anytime.
                        </p>
                    )}
                    {heroState === 'D' && ownerSubState === 'D-gen' && (
                        <p className={styles.detailHero__note}>
                            Safe to leave: this page picks up right here whenever you come back.
                            If you're away when it finishes, we email you the pack.
                        </p>
                    )}
                    {heroState === 'D' && ownerSubState === 'D-del' && (
                        <p className={styles.detailHero__note}>
                            <button type="button" className={styles.inlineLinkButton} onClick={handleRegenerate}>
                                <em>Make it again</em>
                            </button>
                            : your model makes {pack.grid.nImages} new photos. These stay in My Packs.
                        </p>
                    )}

                    {/* Step strip (A/B) · model chip (D/D-gen/D-del) · nothing extra for C
                        (the trainbar lives in the stage). D-del's meta line reads "rendered
                        this · {date}" (copy spec §3c) in place of the default ready/trained
                        line — same chip, no second component. */}
                    {heroState === 'D' && userModel ? (
                        <PackModelChip
                            trainedAt={userModel.trainedAt}
                            photoCount={userModel.photoCount}
                            metaOverride={ownerSubState === 'D-del' && ownerRun ? `made · ${formatBannerDate(ownerRun.createdAt)}` : undefined}
                        />
                    ) : heroState === 'C' ? null : (
                        <PackStepStrip />
                    )}
                </>
            }
            stage={
                <PackHeroStage
                    pack={pack}
                    createVerb={createVerb}
                    stage={stage}
                    // The owner's run, server-resolved (funnel spec §9).
                    slots={dSubState?.slots}
                    onGenerate={() => void fireWallPackContinue('one_click')}
                    onRegenerate={handleRegenerate}
                    onDoneCta={heroState === 'D' ? handleDownloadAll : undefined}
                    ownerRunDate={ownerRun ? formatBannerDate(ownerRun.createdAt) : undefined}
                    userModel={userModel}
                    onTrain={openIntake}
                    coda={coda}
                    lookDoor={lookDoor}
                />
            }
            related={
                // Related strip — part of the persistent document, not a hero-band flip (copy
                // spec §3 line 20): the strip itself never disappears, only its labels vary. D
                // owns its own owner-framed labels; A/B/C share the A form. Spine réclame Wave 1:
                // D-title "More styles…" → "More looks…" (styles is a refused core-register
                // word); D's "generate · ~90s" → "wear this · ~90s" (generate is refused too).
                <PackRelatedStrip
                    title={heroState === 'D' ? 'More looks your model can wear' : relatedTitle}
                    linkLabel={heroState === 'D' ? 'the whole catalog →' : 'one model wears them all →'}
                    linkHref="/packs"
                    cards={relatedPacksCards.map((c) => ({
                        slug: c.slug,
                        title: c.title,
                        cover: c.cover,
                        meta: heroState === 'D' ? 'wear this · ~90s' : `${c.nImages} photos · ${c.categoryLabel.toLowerCase()}`,
                    }))}
                    showWearAction={heroState === 'D'}
                />
            }
            galleryAriaLabel="Pack portrait gallery"
            galleryCaption={<PackGalleryCaption />}
            galleryBody={<PackGalleryRows rows={pack.rows} altBySrc={altBySrc} styles={styles} />}
            afterGallery={
                showReturningBanner && (
                    <div className={styles.returningBuyerBanner} role="note">
                        <span>
                            You bought this pack on {formatBannerDate(recentCompletedGrant!.createdAt)}:{' '}
                            <Link
                                to={`/${pack.slug}?id=${recentCompletedGrant!.id}`}
                                className={styles.returningBuyerLink}
                            >
                                view your {pack.grid.nImages} shots →
                            </Link>
                        </span>
                        <button
                            type="button"
                            className={styles.returningBuyerDismiss}
                            onClick={handleDismissBanner}
                            aria-label="Dismiss returning buyer banner"
                        >
                            ×
                        </button>
                    </div>
                )
            }
            howItWorks={{subhead: 'Three steps.', steps: [...HOW_IT_WORKS]}}
            seoSections={seoSections}
            legalNote={
                <>
                    All images are AI-generated. By using this service, you confirm you are 18+,
                    that any face photo you upload is your own or used with explicit consent.{' '}
                    <Link to="/terms">Full Terms</Link>
                </>
            }
            overlay={
                intakeOpen && (
                    <div className={styles.intakeLayer}>
                        <IdentityIntake
                            onClose={closeIntake}
                            signedIn={isAuthenticated}
                            signupRedirectTo={intakeReturnTo}
                        />
                    </div>
                )
            }
        />
    )
}
